/**
 * weather — an eased DIRECTIONAL particle stream.
 *
 * Three shapes from one spec (effectLayer.types.ts `WeatherSpec`):
 *
 *  • FIXED-ANCHOR — `layer.x` AND `layer.y` both given. Emit from that exact
 *    point with the spec verbatim. This is the Biome Nexus rising motes
 *    (x:1200, y:745, no mode); its behaviour is preserved unchanged.
 *
 *  • REGION-SPANNING — no x/y, mode `'fall'` or `'rise'`. The stream covers the
 *    screen: origin at the camera region's horizontal centre, y pinned to the
 *    region TOP (`mode:'fall'` — snow/ash/rain raining DOWN) or BOTTOM
 *    (`mode:'rise'` — embers/bubbles/motes rising UP). When a `'water'` scene
 *    region is authored, a rise stream instead emits from its surface top
 *    (`weatherConfig.riseOriginY`) so bubbles break at the water line. spreadX
 *    ≈ region half-width so spawns span the width; emit angle points DOWN (fall)
 *    or UP (rise); fall gets a positive gravityY so flakes accelerate. `windX`
 *    (a steady horizontal drift velocity, px/sec — see smoothEmitterConfig.particleAt)
 *    slants the whole stream sideways for blowing snow / drifting ash.
 *
 *  • SWIRL — `mode:'swirl'`. A vortex/spiral emission: particles spiral outward
 *    from the region centre on an expanding Archimedean spiral (radius grows
 *    linearly, angle rotates `swirlTurns` times over the particle's life). Owns
 *    its tick (the SmoothParticleEmitter's linear bearing can't do spiral
 *    motion); uses the same soft additive glow sprites + pool pattern. A
 *    corrupted/nexus/crimson vortex energy effect. Reduce-motion → particles
 *    placed in a static ring (no rotation).
 *
 * Reduce-motion holds every stream still: each is run forward one lifespan so it fills its path,
 * then never ticks. A region-spanning curtain is re-placed only when the camera crosses into
 * another cell of a half-view grid, so a view at the same spot always shows the same field.
 *
 * Region-spanning DEFAULTS are only applied where the spec leaves a field
 * undefined, so a preset can always override angle/spread/gravity explicitly.
 * Driven off the overlay ticker; detached by the emitter's destroy() on unmount.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { SmoothParticleEmitter, type SmoothEmitterRenderConfig } from '../../vfx/SmoothParticleEmitter';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { riseOriginY } from './weatherConfig';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { seededRand } from '../atmospherePlacement';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const TWO_PI = Math.PI * 2;

/** Swirl defaults — a gentle vortex that fills the screen. */
const SWIRL_DEFAULTS = {
  swirlRadiusMin: 40,
  swirlRadiusMax: 320,
  swirlTurns: 1.5,
  size: 14,
  frequencyMs: 150,
  lifespanMs: 4500,
  alphaEnd: 0.5,
} as const;

interface SwirlParticle {
  sprite: ISprite;
  startAngle: number;
  radiusMin: number;
  radiusMax: number;
  turns: number;
  lifespanMs: number;
  ageMs: number;
  colorStart: number;
  colorEnd?: number;
  alphaEnd: number;
  scaleStart: number;
  scaleEnd: number;
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  return (Math.round(lerp(ar, br, t)) << 16) | (Math.round(lerp(ag, bg, t)) << 8) | Math.round(lerp(ab, bb, t));
}

const buildSwirl: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'weather' || layer.mode !== 'swirl') return null;
  const { region } = ctx;
  const reduceMotion = getMotionPrefs().reduceMotion;

  const cx = (region.x0 + region.x1) / 2;
  const cy = (region.y0 + region.y1) / 2;
  const radiusMin = layer.swirlRadiusMin ?? SWIRL_DEFAULTS.swirlRadiusMin;
  const radiusMax = layer.swirlRadiusMax ?? SWIRL_DEFAULTS.swirlRadiusMax;
  const turns = layer.swirlTurns ?? SWIRL_DEFAULTS.swirlTurns;
  const size = layer.size ?? SWIRL_DEFAULTS.size;
  const frequencyMs = layer.frequencyMs ?? SWIRL_DEFAULTS.frequencyMs;
  const lifespanMs = layer.lifespanMs ?? SWIRL_DEFAULTS.lifespanMs;
  const alphaEnd = layer.alphaEnd ?? SWIRL_DEFAULTS.alphaEnd;
  const color = layer.color ?? 0xff66cc;
  const colorEnd = layer.colorEnd;
  const scaleStart = layer.scaleStart ?? 0.3;
  const scaleEnd = layer.scaleEnd ?? 1.0;

  const particles: SwirlParticle[] = [];
  const pool: ISprite[] = [];
  let elapsedMs = 0;
  let spawnAccumMs = 0;
  let spawnSeed = ctx.seed + 5500;
  let densityScale = 1;
  let destroyed = false;

  function spawnParticle(): void {
    if (destroyed || !vfxBudgetManager.canSpawnShape()) return;
    const seed = spawnSeed += 7.3;
    const startAngle = seededRand(seed) * TWO_PI;
    const rMin = lerp(radiusMin, radiusMax, seededRand(seed + 1) * 0.3);
    const rMax = lerp(radiusMin, radiusMax, 0.6 + seededRand(seed + 2) * 0.4);

    let sprite: ISprite | null | undefined = pool.pop();
    if (!sprite) {
      sprite = createGlowSprite({ x: cx, y: cy, size, color, alpha: 0, textureKey: 'glow_soft', blendMode: 'add', parent: ctx.layerContainer });
      if (!sprite) return;
    } else {
      sprite.setTint(color);
      sprite.setPosition(cx, cy);
      sprite.setAlpha(0);
    }

    particles.push({
      sprite,
      startAngle,
      radiusMin: rMin,
      radiusMax: rMax,
      turns,
      lifespanMs,
      ageMs: 0,
      colorStart: color,
      colorEnd,
      alphaEnd,
      scaleStart,
      scaleEnd,
    });
  }

  if (reduceMotion) {
    // Static ring — place particles at mid-life positions, no tick.
    const count = Math.max(6, Math.floor((lifespanMs / frequencyMs) * densityScale));
    for (let i = 0; i < count; i += 1) {
      spawnSeed += 7.3;
      const startAngle = (i / count) * TWO_PI;
      const r = lerp(radiusMin, radiusMax, 0.5);
      const sprite = createGlowSprite({ x: cx + Math.cos(startAngle) * r, y: cy + Math.sin(startAngle) * r, size, color, alpha: alphaEnd * 0.5, textureKey: 'glow_soft', blendMode: 'add', parent: ctx.layerContainer });
      if (sprite) particles.push({ sprite, startAngle, radiusMin: r, radiusMax: r, turns: 0, lifespanMs, ageMs: 0, colorStart: color, colorEnd, alphaEnd, scaleStart, scaleEnd });
    }
    return makeContentHandle({
      sprites: particles.map(p => p.sprite),
      onDestroy: () => { destroyed = true; particles.forEach(p => p.sprite.destroy()); },
      setDensity: (m) => { densityScale = m; },
    });
  }

  const unsub = ctx.addTick((dtMs: number) => {
    if (destroyed) return;
    elapsedMs += dtMs;
    spawnAccumMs += dtMs * densityScale;

    // Spawn cadence (accumulator-style, no drift).
    while (spawnAccumMs >= frequencyMs) {
      spawnAccumMs -= frequencyMs;
      spawnParticle();
    }

    // Update + cull.
    for (let i = particles.length - 1; i >= 0; i -= 1) {
      const p = particles[i];
      p.ageMs += dtMs;
      const t = p.ageMs / p.lifespanMs;
      if (t >= 1) {
        p.sprite.setAlpha(0);
        pool.push(p.sprite);
        particles.splice(i, 1);
        continue;
      }
      // Archimedean spiral: radius grows linearly, angle rotates.
      const r = lerp(p.radiusMin, p.radiusMax, t);
      const angle = p.startAngle + t * p.turns * TWO_PI;
      p.sprite.setPosition(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
      // Alpha: ramp up over first 30%, fade out over last 70%.
      const RAMP = 0.3;
      const alpha = t <= RAMP
        ? lerp(0, p.alphaEnd, t / RAMP)
        : lerp(p.alphaEnd, 0, (t - RAMP) / (1 - RAMP));
      p.sprite.setAlpha(alpha);
      // Scale + colour ease.
      p.sprite.setScale?.(lerp(p.scaleStart, p.scaleEnd, t));
      if (p.colorEnd !== undefined) {
        p.sprite.setTint(lerpColor(p.colorStart, p.colorEnd, t));
      }
    }
  });

  return makeContentHandle({
    onDestroy: () => { destroyed = true; unsub(); particles.forEach(p => p.sprite.destroy()); pool.forEach(s => s.destroy()); },
    setDensity: (m) => { densityScale = m; },
  });
};

/** Directional defaults for a region-spanning stream (filled only where the
 *  spec is silent, so explicit preset values always win). */
const FALL_DEFAULTS = { angleMinDeg: 80, angleMaxDeg: 100, gravityY: 140 } as const;
const RISE_DEFAULTS = { angleMinDeg: -100, angleMaxDeg: -80 } as const;

const buildWeather: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'weather') return null;

  // ── Swirl mode: vortex/spiral emission (owns its tick). ─────────────────────
  if (layer.mode === 'swirl') return buildSwirl(layer, ctx);

  // ── Fixed-anchor: the Nexus motes. Emit from (x,y) with the spec verbatim. ──
  const reduceMotion = getMotionPrefs().reduceMotion;
  if (typeof layer.x === 'number' && typeof layer.y === 'number') {
    const emitter = new SmoothParticleEmitter({ x: layer.x, y: layer.y }, layer, { depth: Z_LAYERS.ATMOSPHERE, parent: ctx.layerContainer });
    if (reduceMotion) emitter.prewarm();
    else emitter.attachTo(ctx.addTick);
    return makeContentHandle({ onDestroy: () => emitter.destroy(), setDensity: (m) => emitter.setDensityScale(m) });
  }

  // ── Region-spanning: a screen-wide fall/rise curtain. ──────────────────────
  const { x0, y0, x1 } = ctx.region;
  const mode = layer.mode ?? 'rise';
  const originX = (x0 + x1) / 2;
  // RISE streams emit from the bottom and rise UP. When the level authors a
  // 'water' region, rise toward (and emit from) its surface top so bubbles break
  // at the water line — else the camera region bottom (prior behaviour, T7).
  const originY = mode === 'fall' ? y0 : riseOriginY(ctx.region, ctx.scene.regions);
  const halfWidth = Math.max(0, (x1 - x0) / 2);
  const dir = mode === 'fall' ? FALL_DEFAULTS : RISE_DEFAULTS;

  // Caller's explicit knobs win; directional defaults fill the gaps. spreadX
  // spans the region width so the curtain covers the screen (spreadY small —
  // a thin emit band along the top/bottom edge).
  const cfg: SmoothEmitterRenderConfig = {
    ...layer,
    angleMinDeg: layer.angleMinDeg ?? dir.angleMinDeg,
    angleMaxDeg: layer.angleMaxDeg ?? dir.angleMaxDeg,
    spreadX: layer.spreadX ?? halfWidth,
    spreadY: layer.spreadY ?? 6,
    gravityY: layer.gravityY ?? (mode === 'fall' ? FALL_DEFAULTS.gravityY : 0),
  };

  if (reduceMotion) return stillCurtain(cfg, originX, originY, halfWidth, ctx);

  const emitter = new SmoothParticleEmitter({ x: originX, y: originY }, cfg, { depth: Z_LAYERS.ATMOSPHERE, parent: ctx.layerContainer });
  emitter.attachTo(ctx.addTick);
  const trackUnsub = ctx.addTick(() => {
    const v = ctx.getCameraView();
    if (v) emitter.setPosition(v.centerX, originY);
  });
  return makeContentHandle({
    onDestroy: () => {
      trackUnsub();
      emitter.destroy();
    },
    setDensity: (m) => emitter.setDensityScale(m),
  });
};

/** Smallest re-placement cell (px) for a still curtain, so a narrow region does not re-place on every step. */
const STILL_CELL_MIN_PX = 200;

/**
 * Reduce-motion curtain: a full, still field around the camera. It is placed at the camera's cell
 * of a half-view grid and re-placed only when the camera enters another cell, so it never moves
 * while the view holds and always looks the same from the same spot.
 */
function stillCurtain(cfg: SmoothEmitterRenderConfig, originX: number, originY: number, halfWidth: number, ctx: Parameters<EffectBuilder>[1]) {
  const cell = Math.max(STILL_CELL_MIN_PX, halfWidth);
  const snap = (x: number) => Math.round(x / cell) * cell;
  let emitter: SmoothParticleEmitter | null = null;
  let placedAt: number | null = null;
  const place = (x: number) => {
    if (x === placedAt) return;
    emitter?.destroy();
    emitter = new SmoothParticleEmitter({ x, y: originY }, cfg, { depth: Z_LAYERS.ATMOSPHERE, parent: ctx.layerContainer });
    emitter.prewarm();
    placedAt = x;
  };
  place(snap(originX));
  const unsub = ctx.addTick(() => {
    const v = ctx.getCameraView();
    if (v) place(snap(v.centerX));
  });
  return makeContentHandle({
    onDestroy: () => {
      unsub();
      emitter?.destroy();
    },
    setDensity: () => { /* a still field keeps its count */ },
  });
}

registerEffect({ kind: 'weather', build: buildWeather, depth: Z_LAYERS.ATMOSPHERE });
